/* eslint-disable sort-keys */
/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { CallCostsFilterOption } from '../models/call-cost-filter-option';
import { CallCostOrderingOptions, CallCostRegisterOptions } from '../models/call-cost-register-options';
import { GetProviderCallRate, GetProviderCallRateVariables } from '../queries/generated/GetProviderCallRate';
import { getCallCostQuery } from '../queries/GetCallCosts.graphql';

@Injectable({ providedIn: 'root' })
export class CallCostRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getCallCost(
      endCursor: string | null,
      filters: CallCostsFilterOption[] | null,
      quickFilters: CallCostRegisterOptions | null,
      orderingOptions: CallCostOrderingOptions[] | null
   ): Observable<ExecutionResult<GetProviderCallRate>> {
      const filterBuilderFilterExpression: FilterExpression = { filterOptions: [] };

      if (filters && filters.length > 0) {
         filterBuilderFilterExpression.filterOptions = filters.map(this.convertCallCostsFilterOption.bind(this));
      }

      if (quickFilters && quickFilters.searchText) {
         const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
         const searchText = quickFilters.searchText.trim().replace(/-/g, '');

         subExpression.filterOptions.push({
            field: 'destination',
            operation: FilterOperations.Contains,
            value: searchText,
         });
         orderingOptions = [{ field: 'destination', direction: OrderingDirections.Asc }];
         const extensionIdOrNumberRegex = /\d+/;

         const numericSearchText = searchText.match(extensionIdOrNumberRegex)?.[0];

         if (numericSearchText) {
            subExpression.filterOptions.push({
               field: 'code',
               operation: FilterOperations.EqualTo,
               value: numericSearchText,
            });
            subExpression.filterOptions.push({
               field: 'code',
               operation: FilterOperations.Contains,
               value: numericSearchText,
            });
            subExpression.filterOptions.push({
               field: 'rate',
               operation: FilterOperations.EqualTo,
               value: numericSearchText,
            });
            orderingOptions = [];
         }

         filterBuilderFilterExpression.subExpressions = filterBuilderFilterExpression.subExpressions
            ? [...filterBuilderFilterExpression.subExpressions, subExpression]
            : [subExpression];
      }
      if (quickFilters && quickFilters.typeName) {
         const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.And };
         if (quickFilters.typeName) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'providerId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.typeName,
            });
         }
         filterBuilderFilterExpression.subExpressions = filterBuilderFilterExpression.subExpressions
            ? [...filterBuilderFilterExpression.subExpressions, subExpression]
            : [subExpression];
      }
      const variables = this.getCallCostsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetProviderCallRate, GetProviderCallRateVariables>({
         query: getCallCostQuery,
         variables,
      });
   }

   private convertCallCostsFilterOption(option: CallCostsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(filterOption: CallCostsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (filterOption.operation === FilterOperations.HasValue) {
         return filterOption.value;
      }

      switch (filterOption.type) {
         case 'callCost':
            return Array.isArray(filterOption.value)
               ? filterOption.value.map((v) => {
                    return v.providerCallRateId;
                 })
               : filterOption.value?.providerCallRateId || null;

         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return filterOption.value;
      }
   }

   private getCallCostsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetProviderCallRateVariables {
      const variables: GetProviderCallRateVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
